// Pages LÉGALES — publiques, lisibles sans compte.
//
// Six documents : mentions légales (LCEN), confidentialité (RGPD art. 13),
// cookies (art. 82 loi Informatique et Libertés), conditions de la bêta,
// accord de sous-traitance (RGPD art. 28) et politique de sécurité (avec sa
// procédure de signalement de faille, que référence /.well-known/security.txt).
//
// Ils décrivent Zygosight TEL QU'IL EST CONSTRUIT : un seul cookie, aucun
// traceur tiers, hébergement dans l'UE, IA sur Vertex en Belgique. Si l'une de
// ces réalités change (outil d'analyse, autre fournisseur d'IA, e-mails
// transactionnels), le texte correspondant doit changer avec elle.
import type { ReactNode } from 'react';
import { Link } from '../router';
import { Button } from '../components/ui';
import { LogoMark, Wordmark } from '../components/Logo';
import { APP_VERSION } from '../version';
import { setCookieChoice, useCookieChoice } from './cookieConsent';
import {
  EDITOR,
  HOSTS,
  MEDIATOR,
  PRIVACY_CONTACT,
  SECURITY_CONTACT,
  SUBPROCESSORS,
  TERMS_VERSION,
  UPDATED_ON,
  hasPlaceholders,
  isPlaceholder,
} from './legalInfo';
import './legal.css';

export const LEGAL_PAGES = [
  { path: '/mentions-legales', label: 'Mentions légales' },
  { path: '/confidentialite', label: 'Confidentialité' },
  { path: '/cookies', label: 'Cookies' },
  { path: '/conditions', label: 'Conditions de la bêta' },
  { path: '/sous-traitance', label: 'Sous-traitance (DPA)' },
  { path: '/securite', label: 'Sécurité' },
] as const;

/** Une valeur de `legalInfo` : surlignée tant que c'est un emplacement vide. */
function V({ children }: { children: string }) {
  return isPlaceholder(children) ? <mark className="legal-todo">{children}</mark> : <>{children}</>;
}

function Mail({ to }: { to: string }) {
  return <a href={`mailto:${to}`}>{to}</a>;
}

/** Pied de page commun : présent sur la connexion, l'application, les pages
 *  publiques. Le lien vers les informations légales doit être accessible de
 *  PARTOUT, y compris pour qui n'a pas de compte. */
export function LegalFooter({ compact = false }: { compact?: boolean }) {
  return (
    <footer className={`legal-footer${compact ? ' legal-footer-compact' : ''}`}>
      <nav aria-label="Informations légales">
        {LEGAL_PAGES.map((p) => (
          <Link key={p.path} to={p.path}>
            {p.label}
          </Link>
        ))}
      </nav>
      <span className="legal-footer-note">
        {/* Le pied compact est celui des pages publiques : sans compte, pas de suivi du produit. */}
        {compact ? `Zygosight v${APP_VERSION}` : <Link to="/versions">Zygosight v{APP_VERSION}</Link>} · bêta · données hébergées dans l’Union européenne
      </span>
    </footer>
  );
}

function LegalLayout({ path, title, children }: { path: string; title: string; children: ReactNode }) {
  return (
    <div className="legal">
      <header className="legal-head">
        <Link to="/" className="legal-brand">
          <LogoMark />
          <Wordmark />
        </Link>
        <Link to="/" className="zy-retour">
          ← Retour à l’application
        </Link>
      </header>
      <div className="legal-body">
        <nav className="legal-nav" aria-label="Documents légaux">
          {LEGAL_PAGES.map((p) => (
            <Link key={p.path} to={p.path} className={p.path === path ? 'legal-nav-on' : undefined}>
              {p.label}
            </Link>
          ))}
        </nav>
        <main className="legal-doc">
          <h1 className="t-h1">{title}</h1>
          <p className="legal-meta t-mono">
            Version {TERMS_VERSION} · mise à jour le {UPDATED_ON}
          </p>
          {hasPlaceholders() ? (
            <p className="legal-warning" role="note">
              Document en cours de finalisation : les passages surlignés restent à compléter.
            </p>
          ) : null}
          {children}
        </main>
      </div>
      <LegalFooter compact />
    </div>
  );
}

// ---------------------------------------------------------------------------
// MENTIONS LÉGALES — loi n° 2004-575 du 21 juin 2004 (LCEN), art. 6 III.
// ---------------------------------------------------------------------------

export function MentionsLegales() {
  return (
    <LegalLayout path="/mentions-legales" title="Mentions légales">
      <h2>Éditeur du service</h2>
      <dl className="legal-dl">
        <dt>Éditeur</dt>
        <dd><V>{EDITOR.name}</V></dd>
        <dt>Forme juridique</dt>
        <dd><V>{EDITOR.legalForm}</V></dd>
        <dt>Capital social</dt>
        <dd><V>{EDITOR.capital}</V></dd>
        <dt>Adresse</dt>
        <dd><V>{EDITOR.address}</V></dd>
        <dt>Immatriculation</dt>
        <dd><V>{EDITOR.registration}</V></dd>
        <dt>TVA intracommunautaire</dt>
        <dd><V>{EDITOR.vat}</V></dd>
        <dt>Téléphone</dt>
        <dd><V>{EDITOR.phone}</V></dd>
        <dt>E-mail</dt>
        <dd><Mail to={EDITOR.email} /></dd>
        <dt>Directeur de la publication</dt>
        <dd><V>{EDITOR.publicationDirector}</V></dd>
      </dl>

      <h2>Hébergement</h2>
      {HOSTS.map((h) => (
        <dl className="legal-dl" key={h.name}>
          <dt>Hébergeur</dt>
          <dd>{h.name}</dd>
          <dt>Rôle</dt>
          <dd>{h.role}</dd>
          <dt>Adresse</dt>
          <dd><V>{h.address}</V></dd>
          <dt>Téléphone</dt>
          <dd><V>{h.phone}</V></dd>
          <dt>Localisation des données</dt>
          <dd>{h.location}</dd>
        </dl>
      ))}

      <h2>Propriété intellectuelle</h2>
      <p>
        Le service Zygosight, son interface, ses textes et son code sont protégés par le droit d’auteur. Toute
        reproduction sans autorisation est interdite. Les contenus que vous déposez dans le service (études,
        entretiens, verbatims, insights) restent les vôtres : voir les <Link to="/conditions">conditions de la bêta</Link>.
      </p>

      <h2>Données personnelles</h2>
      <p>
        Le traitement de vos données est décrit dans la <Link to="/confidentialite">politique de confidentialité</Link>{' '}
        et la page <Link to="/cookies">cookies</Link>.
      </p>
    </LegalLayout>
  );
}

// ---------------------------------------------------------------------------
// CONFIDENTIALITÉ — RGPD, art. 12 à 14.
// ---------------------------------------------------------------------------

export function Confidentialite() {
  return (
    <LegalLayout path="/confidentialite" title="Politique de confidentialité">
      <p className="legal-lede">
        Zygosight sert à mesurer l’expérience et l’adoption de logiciels, et à en comprendre les raisons : on y
        dépose des études, des entretiens et ce que les gens y disent, et on y recueille des réponses à des
        questionnaires. Ce sont souvent des données personnelles — les vôtres, et celles des personnes
        que vous interrogez. Cette page dit qui en répond, ce qui en est fait, où elles sont, combien de temps,
        et comment exercer vos droits.
      </p>

      <h2>1. Deux rôles distincts</h2>
      <ul>
        <li>
          Pour <strong>votre compte</strong> (identité, connexion, sécurité), l’éditeur de Zygosight (
          <V>{EDITOR.name}</V>) est <strong>responsable du traitement</strong>.
        </li>
        <li>
          Pour les <strong>données de recherche</strong> que votre organisation dépose (entretiens, enregistrements,
          verbatims, personas, réponses au questionnaire de mesure zéro), c’est <strong>votre organisation</strong> qui
          est responsable du traitement. Zygosight agit comme <strong>sous-traitant</strong>, sur ses instructions :
          voir l’<Link to="/sous-traitance">accord de sous-traitance</Link>.
        </li>
      </ul>
      <p>
        Contact pour toute question relative à vos données : <Mail to={PRIVACY_CONTACT} />. Aucun délégué à la
        protection des données n’est désigné à ce jour.
      </p>

      <h2>2. Ce que nous traitons en tant que responsable du traitement</h2>
      <div className="zy-table-cadre">
        <table className="zy-table legal-table">
          <thead>
            <tr>
              <th>Finalité</th>
              <th>Données</th>
              <th>Base légale</th>
              <th>Conservation</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Créer et gérer votre compte, vous authentifier</td>
              <td>Nom, e-mail, organisation, rôle, empreinte du mot de passe (jamais le mot de passe lui-même), date et version des conditions acceptées</td>
              <td>Exécution du contrat (art. 6.1.b)</td>
              <td>Tant que le compte existe ; effacées au plus tard 30 jours après sa suppression ou la fin de la bêta</td>
            </tr>
            <tr>
              <td>Maintenir votre session</td>
              <td>Cookie de session ; en base, seulement l’empreinte du jeton, avec ses dates</td>
              <td>Exécution du contrat</td>
              <td>30 jours au plus ; supprimée à la déconnexion</td>
            </tr>
            <tr>
              <td>Sécuriser le service, détecter les abus</td>
              <td>Adresse IP, date, méthode et adresse de la requête (les jetons des liens publics sont masqués), code de réponse</td>
              <td>Intérêt légitime : protéger le service et ses utilisateurs (art. 6.1.f)</td>
              <td>Journaux techniques : 30 jours. Compteurs anti-abus : en mémoire, 24 heures au plus</td>
            </tr>
            <tr>
              <td>Répondre à vos demandes et à vos retours de bêta</td>
              <td>E-mail, contenu des échanges</td>
              <td>Intérêt légitime : assurer le support et améliorer le service</td>
              <td>12 mois après le dernier échange</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Aucune donnée n’est vendue, louée ou utilisée à des fins publicitaires. Aucun outil de mesure d’audience ni
        traceur publicitaire n’est installé.
      </p>

      <h2>3. Les données de recherche que vous déposez</h2>
      <p>
        Entretiens et leurs enregistrements audio ou vidéo (voix, image), transcriptions, verbatims, personas,
        réponses au questionnaire de mesure zéro, membres d’équipe invités : ces données sont traitées pour le compte de
        l’organisation qui mène l’étude, uniquement pour lui fournir le service.
      </p>
      <ul>
        <li>
          <strong>Vous êtes participant à une étude</strong> (interrogé, enregistré, répondant à un questionnaire) ?
          Adressez vos demandes à l’organisation qui vous a sollicité : elle est responsable du traitement. Si vous nous
          écrivez, nous lui transmettons votre demande.
        </li>
        <li>
          <strong>Vous menez une étude</strong> ? Il vous appartient d’informer les personnes interrogées, de disposer
          d’une base légale et de recueillir leur accord avant tout enregistrement. Zygosight vous fait attester cet
          accord à chaque envoi d’enregistrement. Évitez de déposer des données sensibles (santé, opinions, origine…)
          sans nécessité et sans base légale adaptée.
        </li>
      </ul>

      <h2>4. Intelligence artificielle</h2>
      <p>
        Certaines fonctions s’appuient sur des modèles d’IA (Gemini, via Google Cloud Vertex AI, exécutés dans la région
        europe-west1, en Belgique) : transcription des entretiens, extraction des verbatims, propositions de piliers,
        de protocoles ou de besoins, synthèses. Les éléments proposés par l’IA sont signalés comme tels et doivent être
        relus : ce sont des propositions, pas des constats. Aucune décision produisant des effets juridiques à votre
        égard n’est prise de manière automatisée.
      </p>
      <p>
        Les données envoyées au modèle ne servent qu’à produire la réponse demandée.{' '}
        <V>[À confirmer au regard de vos conditions Google Cloud : Google s’engage à ne pas utiliser les données des clients Vertex AI pour entraîner ses modèles.]</V>
      </p>

      <p>
        Une organisation peut choisir d’utiliser sa propre clé d’API Anthropic (page « Compte »). Dans ce cas, et dans
        ce cas seulement, ses extractions, synthèses et propositions sont produites par les modèles Claude
        d’Anthropic, sur le compte de cette organisation et selon les conditions qu’elle a acceptées auprès
        d’Anthropic ; les extraits envoyés peuvent alors être traités hors de l’Union européenne. La transcription
        des entretiens reste effectuée dans l’Union européenne. Ce choix se retire à tout moment.
      </p>

      <h2>5. Destinataires et sous-traitants</h2>
      <p>
        Vos données ne sont accessibles qu’aux membres autorisés de votre organisation, à l’éditeur pour les besoins du
        service et du support, et aux sous-traitants techniques suivants :
      </p>
      <SubprocessorTable />

      <h2>6. Hébergement et transferts hors de l’Union européenne</h2>
      <p>
        Les données sont hébergées dans l’Union européenne (Belgique et Allemagne). Nos prestataires techniques sont des
        sociétés de droit américain : un accès depuis les États-Unis ne peut être exclu (maintenance, réquisition légale).
        Ces transferts éventuels sont encadrés par les clauses contractuelles types de la Commission européenne et, le
        cas échéant, par le cadre de protection des données UE–États-Unis.{' '}
        <V>[À vérifier pour chaque prestataire : adhésion au Data Privacy Framework et clauses signées.]</V>
      </p>

      <h2>7. Sécurité</h2>
      <p>
        Chiffrement des échanges (HTTPS), mots de passe et jetons stockés sous forme d’empreintes, isolation stricte
        entre organisations, secrets des connecteurs chiffrés, enregistrements dans un stockage privé : le détail est
        décrit dans la <Link to="/securite">politique de sécurité</Link>. En cas de violation de données susceptible
        d’engendrer un risque pour vous, nous le notifions à la CNIL dans les 72 heures et, si le risque est élevé,
        nous vous en informons.
      </p>

      <h2>8. Vos droits</h2>
      <p>
        Vous disposez d’un droit d’accès, de rectification, d’effacement, de limitation, de portabilité et
        d’opposition (pour les traitements fondés sur l’intérêt légitime), ainsi que du droit de définir des directives
        relatives au sort de vos données après votre décès. Écrivez à <Mail to={PRIVACY_CONTACT} /> : nous répondons
        dans un délai d’un mois. Nous pouvons vous demander de justifier votre identité en cas de doute raisonnable.
      </p>
      <p>
        Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL
        (<a href="https://www.cnil.fr" rel="noopener noreferrer" target="_blank">www.cnil.fr</a>, 3 place de Fontenoy,
        TSA 80715, 75334 Paris Cedex 07).
      </p>

      <h2>9. Âge minimum</h2>
      <p>Le service est réservé aux personnes âgées d’au moins 18 ans.</p>

      <h2>10. Évolution de cette politique</h2>
      <p>
        Cette politique peut évoluer avec le service. La version en vigueur et sa date figurent en tête de page ; en cas
        de changement important, vous en serez informé dans l’application.
      </p>
    </LegalLayout>
  );
}

function SubprocessorTable() {
  return (
    <div className="zy-table-cadre">
      <table className="zy-table legal-table">
        <thead>
          <tr>
            <th>Prestataire</th>
            <th>Ce qu’il fait</th>
            <th>Localisation des données</th>
          </tr>
        </thead>
        <tbody>
          {SUBPROCESSORS.map((s) => (
            <tr key={s.name}>
              <td>{s.name}</td>
              <td>{s.purpose}</td>
              <td>{s.location}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// COOKIES — art. 82 de la loi Informatique et Libertés ; lignes directrices et
// recommandation de la CNIL du 17 septembre 2020.
// ---------------------------------------------------------------------------

/** Le choix en cours, et de quoi en changer : revenir sur son accord doit être
 *  aussi simple que de le donner. */
function CookieChoicePanel() {
  const choice = useCookieChoice();
  return (
    <div className="cookie-choice">
      <p>
        <strong>Votre choix : </strong>
        {choice === 'accepted'
          ? 'préférences d’affichage mémorisées.'
          : choice === 'refused'
            ? 'préférences d’affichage non mémorisées.'
            : 'pas encore de réponse.'}
      </p>
      <div className="cookie-banner-actions">
        <Button variant="ghost" onClick={() => setCookieChoice('refused')} disabled={choice === 'refused'}>
          Refuser
        </Button>
        <Button variant="ghost" onClick={() => setCookieChoice('accepted')} disabled={choice === 'accepted'}>
          Accepter
        </Button>
      </div>
    </div>
  );
}

export function Cookies() {
  return (
    <LegalLayout path="/cookies" title="Cookies et stockage local">
      <p className="legal-lede">
        Zygosight n’utilise <strong>aucun cookie publicitaire, aucun outil de mesure d’audience et aucun traceur
        tiers</strong>. Les polices de caractères sont servies par l’application elle-même : votre navigateur ne contacte
        aucun autre site. Le cookie de connexion est strictement nécessaire au service. La mémorisation de vos choix
        d’affichage, elle, est facultative : un bandeau vous demande votre accord, et rien n’est mémorisé avant.
      </p>

      <CookieChoicePanel />

      <h2>Cookie</h2>
      <div className="zy-table-cadre">
        <table className="zy-table legal-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Finalité</th>
              <th>Durée</th>
              <th>Caractéristiques</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="t-mono">ro_session</td>
              <td>Vous garder connecté. Strictement nécessaire : sans lui, le service ne fonctionne pas.</td>
              <td>30 jours au plus ; supprimé à la déconnexion</td>
              <td>Illisible par les scripts de la page (httpOnly), transmis uniquement en HTTPS, jamais envoyé à un autre site</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Stockage local du navigateur</h2>
      <p>
        Si vous l’acceptez, quelques préférences d’affichage sont mémorisées dans votre navigateur, sur votre appareil
        uniquement. Elles ne sont jamais transmises au serveur. Si vous refusez, elles valent seulement pour la page en
        cours.
      </p>
      <div className="zy-table-cadre">
        <table className="zy-table legal-table">
          <thead>
            <tr>
              <th>Clé</th>
              <th>Ce qu’elle retient</th>
              <th>Durée</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="t-mono">ro.cookies</td>
              <td>Votre réponse au bandeau (accepté ou refusé), pour ne pas vous reposer la question</td>
              <td>Jusqu’à ce que vous effaciez les données du site</td>
            </tr>
            <tr>
              <td className="t-mono">ro.theme</td>
              <td>Thème clair ou sombre</td>
              <td>Idem</td>
            </tr>
            <tr>
              <td className="t-mono">ro.portfolio.view</td>
              <td>Affichage des projets en cartes ou en liste</td>
              <td>Idem</td>
            </tr>
            <tr>
              <td className="t-mono">ro.filter.&lt;projet&gt;</td>
              <td>Filtre de phase et de version choisi dans un projet</td>
              <td>Idem</td>
            </tr>
            <tr>
              <td className="t-mono">ro.studies.kind.&lt;projet&gt;</td>
              <td>Filtre par type d’étude choisi dans un projet</td>
              <td>Idem</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Les supprimer</h2>
      <p>
        Vous pouvez effacer cookies et stockage local depuis les réglages de votre navigateur (données de site). Effacer
        le cookie de session vous déconnecte ; effacer le stockage local rétablit l’affichage par défaut.
      </p>
      <p>
        Si un outil de mesure d’audience ou tout autre traceur non nécessaire devait être ajouté, il ne serait déposé
        qu’avec votre consentement préalable, et cette page serait mise à jour.
      </p>
    </LegalLayout>
  );
}

// ---------------------------------------------------------------------------
// CONDITIONS DE LA BÊTA
// ---------------------------------------------------------------------------

export function Conditions() {
  return (
    <LegalLayout path="/conditions" title="Conditions d’utilisation de la bêta">
      <p className="legal-lede">
        Zygosight est en <strong>bêta</strong> : un service en construction, ouvert à l’essai. Ces conditions disent ce
        que vous pouvez en attendre, et ce que nous attendons de vous. En créant un compte, vous les acceptez.
      </p>

      <h2>1. Objet</h2>
      <p>
        Zygosight permet de mesurer l’expérience et l’adoption d’un logiciel, et d’organiser la recherche qui les
        explique : baromètres, études, entretiens et leurs enregistrements, verbatims, personas, tests, insights,
        mesures et score d’adoption. Le service est édité par <V>{EDITOR.name}</V> (voir les{' '}
        <Link to="/mentions-legales">mentions légales</Link>).
      </p>

      <h2>2. Accès à la bêta</h2>
      <ul>
        <li>L’accès est gratuit pendant la bêta. Toute évolution vers un service payant sera annoncée à l’avance et ne s’appliquera qu’avec votre accord.</li>
        <li>Le service est réservé aux personnes âgées d’au moins 18 ans, qui l’utilisent pour elles-mêmes ou pour l’organisation qu’elles représentent et qu’elles sont habilitées à engager.</li>
        <li>Nous pouvons limiter le nombre de comptes, le volume de données ou l’usage des fonctions d’IA, notamment pour maîtriser les coûts et la charge.</li>
      </ul>

      <h2>3. Ce qu’est une bêta</h2>
      <ul>
        <li>Le service est fourni <strong>en l’état</strong>. Des fonctions peuvent changer, être retirées ou mal fonctionner.</li>
        <li>La disponibilité n’est pas garantie : des interruptions, y compris imprévues, peuvent survenir.</li>
        <li>Malgré nos précautions, une perte de données ne peut être exclue. <strong>Conservez vos propres copies</strong> de ce qui compte (enregistrements originaux, transcriptions, livrables).</li>
        <li>Les résultats produits par l’IA peuvent être inexacts ou incomplets : ils doivent être vérifiés avant toute décision.</li>
      </ul>

      <h2>4. Votre compte</h2>
      <p>
        Vous êtes responsable de la confidentialité de votre mot de passe et de l’usage fait de votre compte.
        Prévenez-nous sans délai à <Mail to={SECURITY_CONTACT} /> si vous soupçonnez un accès non autorisé.
        L’administrateur d’une organisation gère les accès de son équipe.
      </p>

      <h2>5. Usage acceptable</h2>
      <p>Vous vous engagez à ne pas :</p>
      <ul>
        <li>déposer des contenus illicites, ou des données personnelles sans base légale ni information des personnes concernées ;</li>
        <li>enregistrer une personne sans son accord ;</li>
        <li>déposer des données sensibles (santé, opinions politiques ou religieuses, orientation sexuelle, origine…) sans nécessité et sans base légale adaptée ;</li>
        <li>tenter d’accéder aux données d’une autre organisation, de contourner les limites techniques, ou de perturber le service (hors signalement de bonne foi, voir la <Link to="/securite">politique de sécurité</Link>) ;</li>
        <li>utiliser le service pour développer un produit concurrent en le copiant, ou en extraire massivement le contenu de manière automatisée.</li>
      </ul>

      <h2>6. Vos contenus</h2>
      <p>
        Les contenus que vous déposez restent votre propriété, ou celle de votre organisation. Vous nous accordez
        uniquement le droit de les héberger, de les traiter et de les afficher dans la mesure nécessaire pour vous
        fournir le service, pendant la durée de celui-ci. Pour les données personnelles qu’ils contiennent, nous agissons
        en sous-traitant, dans les conditions de l’<Link to="/sous-traitance">accord de sous-traitance</Link>.
      </p>
      <p>
        Vos retours sur la bêta (suggestions, signalements) peuvent être utilisés librement pour améliorer le service,
        sans contrepartie.
      </p>

      <h2>7. Responsabilité</h2>
      <p>
        Dans les limites permises par la loi, notre responsabilité ne peut être engagée pour les dommages indirects, ni
        pour une perte de données dont vous n’auriez pas conservé de copie, pendant la bêta gratuite. Rien dans ces
        conditions ne limite la responsabilité en cas de faute lourde ou intentionnelle, ni les droits dont vous
        bénéficiez en tant que consommateur.
      </p>

      <h2>8. Suspension et fin</h2>
      <ul>
        <li>Vous pouvez cesser d’utiliser le service à tout moment et demander la suppression de votre compte à <Mail to={PRIVACY_CONTACT} />.</li>
        <li>Nous pouvons suspendre un compte qui enfreint ces conditions, après vous en avoir informé sauf urgence (sécurité, contenu manifestement illicite).</li>
        <li>À la fin de la bêta, vous serez prévenu au moins 30 jours à l’avance, pour récupérer vos données avant leur suppression.</li>
      </ul>

      <h2>9. Modifications</h2>
      <p>
        Ces conditions peuvent évoluer. Chaque version est datée ; en cas de changement important, votre accord vous
        sera redemandé dans l’application.
      </p>

      <h2>10. Droit applicable et litiges</h2>
      <p>
        Ces conditions sont soumises au droit français. En cas de difficulté, écrivez-nous d’abord : la plupart des
        problèmes se règlent ainsi. Si vous agissez en tant que consommateur, vous pouvez recourir gratuitement au
        médiateur de la consommation : <V>{MEDIATOR}</V>. À défaut d’accord amiable, les tribunaux français sont
        compétents, sous réserve des règles protectrices du consommateur.
      </p>
    </LegalLayout>
  );
}

// ---------------------------------------------------------------------------
// SOUS-TRAITANCE — RGPD, art. 28.
// ---------------------------------------------------------------------------

export function SousTraitance() {
  return (
    <LegalLayout path="/sous-traitance" title="Accord de traitement des données (sous-traitance)">
      <p className="legal-lede">
        Quand votre organisation dépose des données de recherche dans Zygosight, elle est <strong>responsable du
        traitement</strong> et l’éditeur de Zygosight (<V>{EDITOR.name}</V>) est son <strong>sous-traitant</strong>{' '}
        au sens de l’article 28 du RGPD. Cet accord fait partie des{' '}
        <Link to="/conditions">conditions de la bêta</Link> et s’applique dès la création de l’organisation.
      </p>

      <h2>1. Objet et durée</h2>
      <p>
        Le sous-traitant héberge et traite les données de recherche du client pour lui fournir le service Zygosight,
        pendant toute la durée d’utilisation du service.
      </p>

      <h2>2. Nature du traitement</h2>
      <dl className="legal-dl">
        <dt>Opérations</dt>
        <dd>Hébergement, stockage, affichage, recherche, transcription et analyse assistées par IA, export, suppression.</dd>
        <dt>Personnes concernées</dt>
        <dd>Participants aux études (personnes interrogées, observées, testées), répondants au questionnaire de mesure zéro, membres de l’équipe du client.</dd>
        <dt>Catégories de données</dt>
        <dd>Identité ou pseudonyme, fonction, propos recueillis (verbatims, transcriptions), enregistrements audio et vidéo (voix, image), réponses aux questionnaires, observations d’usage, e-mails des membres invités.</dd>
        <dt>Données sensibles</dt>
        <dd>Non prévues par le service. Le client s’engage à n’en déposer qu’en cas de nécessité et avec une base légale adaptée.</dd>
      </dl>

      <h2>3. Engagements du sous-traitant</h2>
      <ul>
        <li>Ne traiter les données que sur instruction documentée du client — l’usage du service et de ses paramètres vaut instruction — et jamais pour son propre compte.</li>
        <li>Garantir la confidentialité : seules les personnes qui en ont besoin pour le service y accèdent, et elles sont tenues au secret.</li>
        <li>Mettre en œuvre les mesures de sécurité décrites dans la <Link to="/securite">politique de sécurité</Link>.</li>
        <li>Ne recourir qu’aux sous-traitants ultérieurs listés ci-dessous ; informer le client de tout ajout ou remplacement au moins 30 jours à l’avance, le client pouvant s’y opposer en cessant d’utiliser le service.</li>
        <li>Héberger les données dans l’Union européenne.</li>
        <li>Aider le client à répondre aux demandes d’exercice des droits des personnes concernées, et lui transmettre toute demande reçue directement.</li>
        <li>Notifier au client toute violation de données le concernant dans les meilleurs délais, et au plus tard 48 heures après en avoir eu connaissance, avec les informations utiles à sa propre notification.</li>
        <li>Aider le client, dans la mesure du possible, pour ses analyses d’impact et ses échanges avec l’autorité de contrôle.</li>
        <li>À la fin du service, restituer les données sur demande puis les supprimer, sauvegardes comprises, dans un délai de 30 jours.</li>
        <li>Mettre à disposition du client les informations nécessaires pour démontrer le respect de ces obligations.</li>
      </ul>

      <h2>4. Engagements du client</h2>
      <ul>
        <li>Disposer d’une base légale pour chaque traitement, et informer les personnes concernées.</li>
        <li>Recueillir l’accord des personnes avant de les enregistrer ; Zygosight lui demande de l’attester à chaque envoi.</li>
        <li>Ne déposer que les données nécessaires à l’étude, et les supprimer quand elles ne le sont plus.</li>
        <li>Gérer les accès de son équipe (rôles, départs).</li>
      </ul>

      <h2>5. Sous-traitants ultérieurs</h2>
      <SubprocessorTable />
      <p>
        Les outils que le client connecte lui-même (Matomo, Jira, Google Analytics, PostHog) ne sont pas des
        sous-traitants de Zygosight : le client les choisit et en reste seul maître. Zygosight y lit seulement des
        mesures agrégées, avec les identifiants que le client fournit.
      </p>
    </LegalLayout>
  );
}

// ---------------------------------------------------------------------------
// SÉCURITÉ — mesures en place, et signalement de faille (RFC 9116).
// ---------------------------------------------------------------------------

export function Securite() {
  return (
    <LegalLayout path="/securite" title="Politique de sécurité">
      <p className="legal-lede">
        Des entretiens, des voix, des visages : ce que vous déposez dans Zygosight mérite d’être protégé sérieusement.
        Voici les mesures en place, et comment nous signaler une faille.
      </p>

      <h2>Mesures en place</h2>
      <ul>
        <li><strong>Hébergement dans l’Union européenne</strong> : application, enregistrements et IA en Belgique, base de données en Allemagne.</li>
        <li><strong>Chiffrement</strong> : tous les échanges en HTTPS (HSTS) ; données chiffrées au repos par nos hébergeurs.</li>
        <li><strong>Isolation entre organisations</strong> : chaque requête est cloisonnée par organisation et par projet, côté serveur ; une suite de tests automatisés vérifie qu’aucune donnée ne peut passer d’un client à un autre.</li>
        <li><strong>Mots de passe</strong> : jamais stockés, seulement une empreinte (scrypt, avec sel). Jetons de session et liens d’invitation stockés eux aussi sous forme d’empreintes.</li>
        <li><strong>Protection contre les abus</strong> : limitation des tentatives de connexion, des créations de compte et des appels à l’IA.</li>
        <li><strong>Enregistrements d’entretien</strong> : stockage privé, jamais d’adresse publique ; chaque lecture passe par l’application, qui vérifie vos droits.</li>
        <li><strong>Secrets des connecteurs</strong> (jetons Matomo, Jira, Google Analytics, PostHog) : chiffrés (AES-256-GCM), jamais réaffichés.</li>
        <li><strong>Navigateur</strong> : politique de sécurité du contenu stricte (aucun script tiers), protection contre l’affichage dans un cadre, cookie de session inaccessible aux scripts.</li>
        <li><strong>Requêtes sortantes</strong> : les adresses fournies par les utilisateurs (connecteurs, analyse de site) sont filtrées pour empêcher l’accès au réseau interne.</li>
        <li><strong>Rôles</strong> : administrateur, responsable, contributeur, observateur ; les écritures sensibles sont réservées, et vérifiées côté serveur.</li>
        <li><strong>Dépendances</strong> : surveillées et mises à jour lorsqu’une vulnérabilité est publiée.</li>
      </ul>

      <h2>Signaler une faille</h2>
      <p>
        Vous pensez avoir trouvé une vulnérabilité ? Écrivez à <Mail to={SECURITY_CONTACT} /> en décrivant le problème
        et comment le reproduire. Nous accusons réception sous 5 jours ouvrés et vous tenons informé de la correction.
        Notre fichier <a href="/.well-known/security.txt">security.txt</a> reprend ces informations.
      </p>
      <p>Pour que votre démarche reste de bonne foi :</p>
      <ul>
        <li>n’accédez qu’à vos propres données ou à des comptes de test ; arrêtez-vous dès la preuve obtenue ;</li>
        <li>ne modifiez, ne supprimez et ne conservez aucune donnée d’autrui ;</li>
        <li>pas de déni de service, pas d’ingénierie sociale, pas de test physique ;</li>
        <li>laissez-nous un délai raisonnable pour corriger avant toute publication.</li>
      </ul>
      <p>
        Dans ces conditions, nous n’engagerons aucune poursuite contre vous. Pendant la bêta, il n’y a pas de programme
        de récompense, mais nous serons heureux de vous remercier publiquement si vous le souhaitez.
      </p>
    </LegalLayout>
  );
}

/** Aiguillage : la page légale correspondant à un chemin, ou `null`. */
export function legalPageFor(path: string): ReactNode | null {
  switch (path) {
    case '/mentions-legales':
      return <MentionsLegales />;
    case '/confidentialite':
      return <Confidentialite />;
    case '/cookies':
      return <Cookies />;
    case '/conditions':
      return <Conditions />;
    case '/sous-traitance':
      return <SousTraitance />;
    case '/securite':
      return <Securite />;
    default:
      return null;
  }
}
